import React, { useEffect, useRef } from 'react';
import { VideoRTC } from '../vendor/video-rtc';

const ELEMENT = 'eye-preview-rtc';

if (typeof window !== 'undefined' && !customElements.get(ELEMENT)) {
  class EyePreviewRTC extends VideoRTC {
    constructor() {
      super();
      this._eyeMuted = true;
      // Vendor default is 15s. A replaced socket must reconnect inside the preview budget.
      this.RECONNECT_TIMEOUT = 2000;
    }

    set eyeMuted(value) {
      this._eyeMuted = !!value;
      this.applyEyeMuted();
    }

    get eyeMuted() {
      return this._eyeMuted;
    }

    applyEyeMuted() {
      if (!this.video) return;
      this.video.muted = this._eyeMuted;
      this.video.volume = this._eyeMuted ? 0 : 1;
    }

    oninit() {
      super.oninit();
      if (this.video) {
        this.video.controls = false;
        this.video.playsInline = true;
        this.video.style.objectFit = 'contain';
        this.video.style.background = '#111';
        this.applyEyeMuted();
      }
    }

    ondisconnect() {
      if (this.reconnectTID) {
        clearTimeout(this.reconnectTID);
        this.reconnectTID = 0;
      }
      super.ondisconnect();
    }

    onconnect() {
      if (!this.isConnected || !this.wsURL || this.ws || this.pc) return false;
      this.wsState = WebSocket.CONNECTING;
      this.connectTS = Date.now();
      this.ws = new WebSocket(this.wsURL);
      this.ws.binaryType = 'arraybuffer';
      this.ws.addEventListener('open', () => this.onopen());
      this.ws.addEventListener('close', (ev) => {
        // Quality/src changes close the previous socket after the next one is
        // already assigned. That late close must not drop the new socket or
        // arm the reconnect timer.
        if (ev.target !== this.ws) return;
        const detail = { code: ev.code, reason: ev.reason || '' };
        this._eyeLastClose = detail;
        this.dispatchEvent(new CustomEvent('eye-ws-close', { detail }));
        this.onclose();
      });
      return true;
    }

    onclose() {
      const last = this._eyeLastClose || {};
      if (last.code === 1011 || last.code === 4401 || last.code === 4404) {
        this.wsState = WebSocket.CLOSED;
        this.ws = null;
        return false;
      }
      return super.onclose();
    }

    /** Respect user unmute — only fall back to muted autoplay when sound is off. */
    play() {
      if (!this.video) return;
      this.applyEyeMuted();
      this.video.play().catch(() => {
        if (this._eyeMuted) return;
        this.video.muted = true;
        this.video.volume = 0;
        this.video.play().catch(() => {});
      });
    }
  }
  customElements.define(ELEMENT, EyePreviewRTC);
}

function applyMutedToHost(host, muted) {
  if (!host) return null;
  host.eyeMuted = muted;
  const video = host.video || host.querySelector?.('video');
  if (video) {
    video.muted = muted;
    video.volume = muted ? 0 : 1;
  }
  return video;
}

/**
 * Props for ConfiguredMsePlayer.
 *
 * @typedef {Object} ConfiguredMsePlayerProps
 * @property {string} src - The stream URL used to connect to the media source.
 * @property {boolean} [hidden=false] - Whether to hide the player visually while keeping it mounted.
 * @property {boolean} [muted=true] - Whether the video and audio are muted. Changing this does not reconnect the stream.
 * @property {boolean} [paused=false] - Whether playback is paused. When paused, captures the current video frame.
 * @property {() => void} [onPlaying] - Callback invoked when playback starts or the stream becomes ready.
 * @property {(error: { message?: string, code?: number, [key: string]: any }) => void} [onError] - Callback invoked when a playback or stream error occurs.
 * @property {(frame: string | null) => void} [onPauseFrame] - Callback receiving the paused frame as a JPEG data URL, or null if capture is unavailable.
 */

/**
 * Configured MSE/WebRTC stream player.
 * ------------------------------------
 * 
 * @param {ConfiguredMsePlayerProps} props - The player configuration.
 * @returns {React.ReactElement} The configured media player element.
 */
export default function ConfiguredMsePlayer({
  src,
  hidden = false,
  muted = true,
  paused = false,
  onPlaying,
  onError,
  onPauseFrame,
}) {
  const hostRef = useRef(null);
  const mutedRef = useRef(muted);
  const pausedRef = useRef(paused);
  const onPlayingRef = useRef(onPlaying);
  const onErrorRef = useRef(onError);
  const onPauseFrameRef = useRef(onPauseFrame);
  onPlayingRef.current = onPlaying;
  onErrorRef.current = onError;
  onPauseFrameRef.current = onPauseFrame;
  mutedRef.current = muted;
  pausedRef.current = paused;

  // Connect/disconnect WebSocket only when src changes — never on mute toggle.
  useEffect(() => {
    const el = hostRef.current;
    if (!el || !src) return undefined;

    el.mode = 'mse,webrtc';
    el.media = 'video,audio';
    el.background = true;
    el.visibilityCheck = false;
    el.eyeMuted = mutedRef.current;
    el.src = src;

    let played = false;
    let errorNotified = false;
    const notifyPlaying = () => {
      if (played) return;
      played = true;
      errorNotified = false;
      applyMutedToHost(el, mutedRef.current);
      if (onPlayingRef.current) onPlayingRef.current();
    };
    // Video-element errors: report once, and not after frames already arrived.
    const notifyError = (detail) => {
      if (played || errorNotified) return;
      errorNotified = true;
      if (onErrorRef.current) onErrorRef.current(detail || {});
    };
    // An abnormal WebSocket close is a definitive stream loss, so it is reported
    // even after playback started — that drives the caller's reconnect ladder.
    const handleWsClose = (ev) => {
      const detail = ev?.detail || {};
      const code = Number(detail.code || 0);
      if (code === 1000 || code === 1001 || code === 1005) return;
      played = false;
      errorNotified = true;
      if (onErrorRef.current) onErrorRef.current(detail);
    };

    el.addEventListener('eye-ws-close', handleWsClose);

    const bindVideo = (video) => {
      if (!video) return () => {};
      applyMutedToHost(el, mutedRef.current);
      video.playsInline = true;
      const onPlay = () => notifyPlaying();
      const onLoaded = () => {
        if (video.videoWidth > 0 || video.readyState >= 2) notifyPlaying();
      };
      const onErr = () => notifyError();
      video.addEventListener('playing', onPlay);
      video.addEventListener('loadeddata', onLoaded);
      video.addEventListener('error', onErr);
      return () => {
        video.removeEventListener('playing', onPlay);
        video.removeEventListener('loadeddata', onLoaded);
        video.removeEventListener('error', onErr);
      };
    };

    let boundVideo = el.video || el.querySelector('video') || null;
    let unbind = bindVideo(boundVideo);
    const rebindIfChanged = () => {
      const video = el.video || el.querySelector('video');
      if (!video || video === boundVideo) return;
      unbind();
      boundVideo = video;
      unbind = bindVideo(video);
    };

    // Video may be created in connectedCallback before or right after src is set.
    const retryBind = window.setTimeout(rebindIfChanged, 50);

    // Keep watching for a late <video> so listeners always get attached.
    let bindAttempts = 0;
    const bindPoll = window.setInterval(() => {
      bindAttempts += 1;
      rebindIfChanged();
      if (boundVideo || bindAttempts > 40) window.clearInterval(bindPoll);
    }, 100);

    // If frames are flowing but `playing` was missed, still surface readiness.
    const poll = window.setInterval(() => {
      const video = el.video || el.querySelector('video');
      if (!video) return;
      applyMutedToHost(el, mutedRef.current);
      if (video.videoWidth > 0 && video.readyState >= 2 && !video.paused) {
        notifyPlaying();
      } else if (!pausedRef.current && video.videoWidth > 0 && video.readyState >= 2) {
        video.play?.().catch(() => {});
        notifyPlaying();
      }
    }, 500);

    return () => {
      window.clearTimeout(retryBind);
      window.clearInterval(bindPoll);
      window.clearInterval(poll);
      unbind();
      el.removeEventListener('eye-ws-close', handleWsClose);
      try {
        // Stop MSE first so pending SourceBuffer updateend won't touch a detached buffer.
        if (el.video) {
          el.video.pause?.();
          el.video.removeAttribute('src');
          el.video.srcObject = null;
          el.video.load?.();
        }
      } catch (_) {
        /* ignore */
      }
      try {
        if (typeof el.ondisconnect === 'function') {
          el.ondisconnect();
        }
      } catch (_) {
        /* ignore MSE teardown races */
      }
    };
  }, [src]);

  useEffect(() => {
    const el = hostRef.current;
    const video = el?.video || el?.querySelector('video');
    if (!video) {
      if (paused) onPauseFrameRef.current?.(null);
      return;
    }
    if (!paused) {
      video.play?.().catch(() => {});
      return;
    }
    if (paused) {
      video.pause();
      if (!video.videoWidth || !video.videoHeight) {
        onPauseFrameRef.current?.(null);
        return;
      }
      try {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const context = canvas.getContext('2d');
        if (!context) throw new Error('Unable to create a canvas context for the stream frame');
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        onPauseFrameRef.current?.(canvas.toDataURL('image/jpeg', 0.88));
      } catch (error) {
        onErrorRef.current?.({ message: error.message || 'Unable to capture the paused stream frame' });
        onPauseFrameRef.current?.(null);
      }
    }
  }, [paused]);

  // UI-only mute — does not reconnect go2rtc / configured-preview-mse.
  useEffect(() => {
    const el = hostRef.current;
    if (!el) return undefined;
    const video = applyMutedToHost(el, muted);
    if (!video) return undefined;
    if (!muted && video.paused) {
      video.play().catch(() => {});
    }
    return undefined;
  }, [muted]);

  const style = hidden
    ? {
        position: 'absolute',
        width: 1,
        height: 1,
        opacity: 0,
        pointerEvents: 'none',
        overflow: 'hidden',
      }
    : {
        display: 'block',
        width: '100%',
        height: '100%',
      };

  return React.createElement(ELEMENT, { ref: hostRef, style });
}
